/**
 * Industry Constants
 *
 * Shared industry data for onboarding and business settings.
 * Single source of truth for industry IDs, labels, and categories.
 */

export interface Industry {
  id: string;
  label: string;
  category: string;
}

/**
 * Get the display label for an industry ID.
 * Handles 'other' industry with custom text, and falls back to the ID if not found.
 */
export function getIndustryLabel(industryId: string, customIndustry?: string): string {
  if (industryId === 'other' && customIndustry) {
    return customIndustry;
  }
  const industry = ALL_INDUSTRIES.find((i) => i.id === industryId);
  return industry?.label || industryId;
}

/**
 * Get full industry object by ID
 */
export function getIndustryById(industryId: string): Industry | undefined {
  return ALL_INDUSTRIES.find((i) => i.id === industryId);
}

// Popular industries shown as quick-select chips
export const POPULAR_INDUSTRIES = [
  'general_contractor',
  'plumbing',
  'electrical',
  'cleaning',
  'landscaping',
];

// Comprehensive list of industries/trades for service businesses
export const ALL_INDUSTRIES: Industry[] = [
  // Construction & Trades
  { id: 'general_contractor', label: 'General Contractor', category: 'Construction' },
  { id: 'subcontractor', label: 'Subcontractor', category: 'Construction' },
  { id: 'carpentry', label: 'Carpentry', category: 'Construction' },
  { id: 'roofing', label: 'Roofing', category: 'Construction' },
  { id: 'siding', label: 'Siding', category: 'Construction' },
  { id: 'masonry', label: 'Masonry & Brickwork', category: 'Construction' },
  { id: 'concrete', label: 'Concrete', category: 'Construction' },
  { id: 'demolition', label: 'Demolition', category: 'Construction' },
  { id: 'excavation', label: 'Excavation', category: 'Construction' },
  { id: 'drywall', label: 'Drywall', category: 'Construction' },
  { id: 'insulation', label: 'Insulation', category: 'Construction' },
  { id: 'fencing', label: 'Fencing', category: 'Construction' },
  { id: 'decks', label: 'Decks & Patios', category: 'Construction' },
  { id: 'welding', label: 'Welding', category: 'Construction' },

  // Plumbing
  { id: 'plumbing', label: 'Plumbing', category: 'Plumbing' },
  { id: 'drain_cleaning', label: 'Drain Cleaning', category: 'Plumbing' },
  { id: 'septic', label: 'Septic Services', category: 'Plumbing' },
  { id: 'irrigation', label: 'Irrigation & Sprinklers', category: 'Plumbing' },

  // Electrical
  { id: 'electrical', label: 'Electrical', category: 'Electrical' },
  { id: 'solar', label: 'Solar Installation', category: 'Electrical' },
  { id: 'generator', label: 'Generator Services', category: 'Electrical' },
  { id: 'lighting', label: 'Lighting Installation', category: 'Electrical' },

  // HVAC
  { id: 'hvac', label: 'HVAC', category: 'HVAC' },
  { id: 'refrigeration', label: 'Refrigeration', category: 'HVAC' },

  // Cleaning
  { id: 'cleaning', label: 'Cleaning Services', category: 'Cleaning' },
  { id: 'house_cleaning', label: 'House Cleaning', category: 'Cleaning' },
  { id: 'carpet_cleaning', label: 'Carpet Cleaning', category: 'Cleaning' },
  { id: 'window_cleaning', label: 'Window Cleaning', category: 'Cleaning' },
  { id: 'pressure_washing', label: 'Pressure Washing', category: 'Cleaning' },
  { id: 'janitorial', label: 'Janitorial', category: 'Cleaning' },

  // Landscaping & Outdoor
  { id: 'landscaping', label: 'Landscaping', category: 'Outdoor' },
  { id: 'lawn_care', label: 'Lawn Care', category: 'Outdoor' },
  { id: 'tree_service', label: 'Tree Service', category: 'Outdoor' },
  { id: 'snow_removal', label: 'Snow Removal', category: 'Outdoor' },
  { id: 'pool_service', label: 'Pool Service', category: 'Outdoor' },

  // Painting & Flooring
  { id: 'painting', label: 'Painting', category: 'Finishing' },
  { id: 'flooring', label: 'Flooring', category: 'Finishing' },
  { id: 'tile', label: 'Tile Installation', category: 'Finishing' },

  // Home Improvement
  { id: 'handyman', label: 'Handyman', category: 'Home Improvement' },
  { id: 'remodeling', label: 'Remodeling', category: 'Home Improvement' },
  { id: 'garage_doors', label: 'Garage Doors', category: 'Home Improvement' },
  { id: 'doors_windows', label: 'Doors & Windows', category: 'Home Improvement' },
  { id: 'cabinets', label: 'Cabinets', category: 'Home Improvement' },

  // Specialty Services
  { id: 'locksmith', label: 'Locksmith', category: 'Specialty' },
  { id: 'appliance_repair', label: 'Appliance Repair', category: 'Specialty' },
  { id: 'chimney', label: 'Chimney Services', category: 'Specialty' },
  { id: 'pest_control', label: 'Pest Control', category: 'Specialty' },
  { id: 'restoration', label: 'Restoration Services', category: 'Specialty' },
  { id: 'glass_glazing', label: 'Glass & Glazing', category: 'Specialty' },

  // Technology
  { id: 'it_consulting', label: 'IT Consulting', category: 'Technology' },
  { id: 'software_development', label: 'Software Development', category: 'Technology' },
  { id: 'web_development', label: 'Web Development', category: 'Technology' },
  { id: 'web_design', label: 'Web Design', category: 'Technology' },
  { id: 'computer_repair', label: 'Computer Repair', category: 'Technology' },
  { id: 'security_systems', label: 'Security Systems', category: 'Technology' },
  { id: 'home_automation', label: 'Home Automation', category: 'Technology' },
  { id: 'networking', label: 'Networking', category: 'Technology' },

  // Automotive
  { id: 'auto_repair', label: 'Auto Repair', category: 'Automotive' },
  { id: 'auto_detailing', label: 'Auto Detailing', category: 'Automotive' },
  { id: 'towing', label: 'Towing', category: 'Automotive' },
  { id: 'car_rental', label: 'Car Rental', category: 'Automotive' },

  // Moving & Logistics
  { id: 'moving', label: 'Moving Services', category: 'Moving & Logistics' },
  { id: 'junk_removal', label: 'Junk Removal', category: 'Moving & Logistics' },
  { id: 'hauling', label: 'Hauling', category: 'Moving & Logistics' },
  { id: 'delivery', label: 'Delivery Services', category: 'Moving & Logistics' },
  { id: 'courier', label: 'Courier', category: 'Moving & Logistics' },
  { id: 'freight', label: 'Freight', category: 'Moving & Logistics' },
  { id: 'trucking', label: 'Trucking', category: 'Moving & Logistics' },
  { id: 'shipping', label: 'Shipping', category: 'Moving & Logistics' },

  // Professional Services
  { id: 'consulting', label: 'Consulting', category: 'Professional' },
  { id: 'accounting', label: 'Accounting', category: 'Professional' },
  { id: 'bookkeeping', label: 'Bookkeeping', category: 'Professional' },
  { id: 'attorney', label: 'Attorney / Legal', category: 'Professional' },
  { id: 'notary', label: 'Notary', category: 'Professional' },
  { id: 'architecture', label: 'Architecture', category: 'Professional' },
  { id: 'engineering', label: 'Engineering', category: 'Professional' },
  { id: 'inspection', label: 'Home Inspection', category: 'Professional' },
  { id: 'property_mgmt', label: 'Property Management', category: 'Professional' },
  { id: 'real_estate', label: 'Real Estate', category: 'Professional' },
  { id: 'interior_design', label: 'Interior Design', category: 'Professional' },
  { id: 'tax_services', label: 'Tax Services', category: 'Professional' },
  { id: 'financial_planning', label: 'Financial Planning', category: 'Professional' },
  { id: 'insurance', label: 'Insurance', category: 'Professional' },

  // Creative & Design
  { id: 'graphic_design', label: 'Graphic Design', category: 'Creative' },
  { id: 'logo_design', label: 'Logo Design', category: 'Creative' },
  { id: 'photography', label: 'Photography', category: 'Creative' },
  { id: 'videography', label: 'Videography', category: 'Creative' },
  { id: 'video_production', label: 'Video Production', category: 'Creative' },
  { id: 'animation', label: 'Animation', category: 'Creative' },
  { id: 'sign_making', label: 'Sign Making', category: 'Creative' },

  // Marketing
  { id: 'marketing', label: 'Marketing', category: 'Marketing' },
  { id: 'social_media', label: 'Social Media', category: 'Marketing' },
  { id: 'seo', label: 'SEO', category: 'Marketing' },
  { id: 'advertising', label: 'Advertising', category: 'Marketing' },
  { id: 'copywriting', label: 'Copywriting', category: 'Marketing' },

  // Writing
  { id: 'freelance_writing', label: 'Freelance Writing', category: 'Writing' },
  { id: 'editing', label: 'Editing', category: 'Writing' },
  { id: 'translation', label: 'Translation', category: 'Writing' },
  { id: 'transcription', label: 'Transcription', category: 'Writing' },

  // Events
  { id: 'event_planning', label: 'Event Planning', category: 'Events' },
  { id: 'catering', label: 'Catering', category: 'Events' },
  { id: 'dj_services', label: 'DJ Services', category: 'Events' },
  { id: 'florist', label: 'Florist', category: 'Events' },
  { id: 'bartending', label: 'Bartending', category: 'Events' },
  { id: 'musician', label: 'Musician', category: 'Events' },
  { id: 'party_rentals', label: 'Party Rentals', category: 'Events' },

  // Food & Beverage
  { id: 'bakery', label: 'Bakery', category: 'Food & Beverage' },
  { id: 'food_truck', label: 'Food Truck', category: 'Food & Beverage' },
  { id: 'personal_chef', label: 'Personal Chef', category: 'Food & Beverage' },
  { id: 'restaurant', label: 'Restaurant', category: 'Food & Beverage' },

  // Health & Wellness
  { id: 'personal_training', label: 'Personal Training', category: 'Health & Wellness' },
  { id: 'massage', label: 'Massage Therapy', category: 'Health & Wellness' },
  { id: 'therapy', label: 'Therapy / Counseling', category: 'Health & Wellness' },
  { id: 'chiropractic', label: 'Chiropractic', category: 'Health & Wellness' },
  { id: 'yoga', label: 'Yoga Instruction', category: 'Health & Wellness' },
  { id: 'nutrition', label: 'Nutrition', category: 'Health & Wellness' },
  { id: 'life_coaching', label: 'Life Coaching', category: 'Health & Wellness' },
  { id: 'home_health', label: 'Home Health', category: 'Health & Wellness' },
  { id: 'medical', label: 'Medical Services', category: 'Health & Wellness' },
  { id: 'dental', label: 'Dental Services', category: 'Health & Wellness' },

  // Beauty
  { id: 'hair_salon', label: 'Hair Salon', category: 'Beauty' },
  { id: 'barbershop', label: 'Barbershop', category: 'Beauty' },
  { id: 'nail_salon', label: 'Nail Salon', category: 'Beauty' },
  { id: 'makeup_artist', label: 'Makeup Artist', category: 'Beauty' },
  { id: 'esthetician', label: 'Esthetician', category: 'Beauty' },
  { id: 'lash_extensions', label: 'Lash Extensions', category: 'Beauty' },
  { id: 'tattoo', label: 'Tattoo Artist', category: 'Beauty' },
  { id: 'spa', label: 'Spa Services', category: 'Beauty' },

  // Pet Services
  { id: 'dog_walking', label: 'Dog Walking', category: 'Pet Services' },
  { id: 'pet_sitting', label: 'Pet Sitting', category: 'Pet Services' },
  { id: 'pet_grooming', label: 'Pet Grooming', category: 'Pet Services' },
  { id: 'dog_training', label: 'Dog Training', category: 'Pet Services' },
  { id: 'veterinary', label: 'Veterinary', category: 'Pet Services' },

  // Childcare & Education
  { id: 'daycare', label: 'Daycare', category: 'Childcare & Education' },
  { id: 'babysitting', label: 'Babysitting', category: 'Childcare & Education' },
  { id: 'nanny', label: 'Nanny Services', category: 'Childcare & Education' },
  { id: 'tutoring', label: 'Tutoring', category: 'Childcare & Education' },
  { id: 'music_lessons', label: 'Music Lessons', category: 'Childcare & Education' },
  { id: 'driving_lessons', label: 'Driving Lessons', category: 'Childcare & Education' },
  { id: 'sports_coaching', label: 'Sports Coaching', category: 'Childcare & Education' },

  // Retail & E-Commerce
  { id: 'ecommerce', label: 'E-Commerce', category: 'Retail' },
  { id: 'retail', label: 'Retail', category: 'Retail' },
  { id: 'wholesale', label: 'Wholesale', category: 'Retail' },
  { id: 'rental_services', label: 'Rental Services', category: 'Retail' },

  // Hospitality
  { id: 'hospitality', label: 'Hospitality', category: 'Hospitality' },
  { id: 'vacation_rental', label: 'Vacation Rental', category: 'Hospitality' },
  { id: 'airbnb_mgmt', label: 'Airbnb Management', category: 'Hospitality' },

  // Agriculture
  { id: 'farming', label: 'Farming', category: 'Agriculture' },
  { id: 'ranching', label: 'Ranching', category: 'Agriculture' },
  { id: 'nursery', label: 'Plant Nursery', category: 'Agriculture' },

  // Manufacturing
  { id: 'manufacturing', label: 'Manufacturing', category: 'Manufacturing' },
  { id: 'fabrication', label: 'Metal Fabrication', category: 'Manufacturing' },
  { id: 'woodworking', label: 'Woodworking', category: 'Manufacturing' },
  { id: 'upholstery', label: 'Upholstery', category: 'Manufacturing' },

  // Marine
  { id: 'boat_repair', label: 'Boat Repair', category: 'Marine' },
  { id: 'marine_services', label: 'Marine Services', category: 'Marine' },

  // Business Types
  { id: 'freelancer', label: 'Freelancer', category: 'Business Type' },
  { id: 'self_employed', label: 'Self-Employed', category: 'Business Type' },
  { id: 'sole_trader', label: 'Sole Trader', category: 'Business Type' },
  { id: 'contractor', label: 'Contractor', category: 'Business Type' },
  { id: 'small_business', label: 'Small Business', category: 'Business Type' },
  { id: 'non_profit', label: 'Non-Profit', category: 'Business Type' },
  { id: 'agency', label: 'Agency', category: 'Business Type' },

  // Miscellaneous
  { id: 'electronics_repair', label: 'Electronics Repair', category: 'Miscellaneous' },
  { id: 'tailor', label: 'Tailor / Alterations', category: 'Miscellaneous' },
  { id: 'dry_cleaning', label: 'Dry Cleaning', category: 'Miscellaneous' },
  { id: 'jewelry_repair', label: 'Jewelry Repair', category: 'Miscellaneous' },
  { id: 'personal_assistant', label: 'Personal Assistant', category: 'Miscellaneous' },
  { id: 'organizing', label: 'Professional Organizing', category: 'Miscellaneous' },
  { id: 'errand_services', label: 'Errand Services', category: 'Miscellaneous' },

  // Other
  { id: 'other', label: 'Other', category: 'Other' },
];
